import {Card, useDialog} from 'platform/components';
import {Box} from 'platform/foundation';

import {useCallback} from 'react';

import {mergeAll, not} from 'ramda';

import {
  DOCUMENT_ACTION_COLUMNS,
  useGetDocumentDatagridActions,
} from '@dms/features/document-context';
import i18n from '@dms/i18n';
import {
  DmsDataGrid,
  Section,
  UploadDocumentsDialog,
  refreshDmsDatagrid,
  usePermissions,
} from '@dms/shared';
import {useWarehouseParams} from '@dms/warehouse/shared-components';

import type {QueryFilterObject} from 'features/eag-grid';

interface DocumentsProps {
  onDocumentAction: VoidFunction;
}

export function Documents(props: DocumentsProps) {
  const {transferOrderId} = useWarehouseParams();

  const [canEditTransferOrder] = usePermissions({
    permissionKeys: ['transferOrderEdit'],
  });

  const [rowActions] = useGetDocumentDatagridActions({
    contextTarget: 'warehouse-transfer-order',
    contextId: transferOrderId,
    refreshData: () => refreshDmsDatagrid(DOCUMENTS_GRID_CODE),
  });

  const [isUploadDialogOpen, openUploadDialog, closeUploadDialog] = useDialog();

  const queryModifier = useCallback(
    (filter: QueryFilterObject) => mergeAll([filter, {contextTargetId: transferOrderId}]),
    [transferOrderId]
  );

  return (
    <Section>
      <Card
        title={i18n.t('page.warehouse.labels.documents')}
        actions={[
          {
            isDisabled: not(canEditTransferOrder),
            type: 'button',
            variant: 'link',
            leftIcon: 'action/backup',
            title: i18n.t('entity.document.actions.uploadDocument'),
            onClick: openUploadDialog,
          },
        ]}
      >
        <Box height="100%">
          <DmsDataGrid
            gridCode={DOCUMENTS_GRID_CODE}
            emptyState={{headline: i18n.t('general.labels.noData')}}
            actionCallback={rowActions}
            predefinedColumns={DOCUMENT_ACTION_COLUMNS}
            queryModifier={queryModifier}
            autoHeight
            data-testid={DOCUMENTS_GRID_CODE}
          />
        </Box>
      </Card>

      <UploadDocumentsDialog
        isOpen={isUploadDialogOpen}
        handleClose={closeUploadDialog}
        onDocumentCreated={() => {
          refreshDmsDatagrid(DOCUMENTS_GRID_CODE);
          props.onDocumentAction();
        }}
        contextTarget="warehouse-transfer-order"
        contextId={transferOrderId}
        bulkContext={[{target: 'warehouse-transfer-order', targetId: transferOrderId}]}
      />
    </Section>
  );
}

const DOCUMENTS_GRID_CODE = 'document-context-list-warehouse-transfer-order';
